import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type {
    InfiniteScrollListCursor,
    InfiniteScrollListPredicateDescriptorSet,
} from '@/components/ui/infinite-scroll-list.types';
import { ListPredicateControls } from '@/components/ui/list-predicate-controls';
import type {
    SearchFieldPredicate,
    SearchJsonPathPredicate,
} from '@anchorbase/pillar-sdk';
import {
    DefaultInfiniteScrollListStateCopy,
    InfiniteScrollListEmptyState,
    InfiniteScrollListErrorState,
    InfiniteScrollListLoadingState,
    type InfiniteScrollListStateCopy,
} from '@/components/ui/infinite-scroll-list-state';
import {
    useInfiniteScrollList,
    type UseInfiniteScrollListOptions,
} from '@/hooks/useInfiniteScrollList';

interface InfiniteScrollListRenderContext<
    TItem,
    TCursor extends InfiniteScrollListCursor,
> {
    readonly items: ReadonlyArray<TItem>;
    readonly isFetching: boolean;
    readonly isFetchingNextPage: boolean;
    readonly hasNextPage: boolean;
    readonly pagination: ReturnType<
        typeof useInfiniteScrollList<TItem, TCursor, readonly unknown[]>
    >['pagination'];
}

export interface InfiniteScrollListProps<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
> extends Pick<
        UseInfiniteScrollListOptions<TItem, TCursor, TQueryKeyPrefix>,
        | 'queryKeyPrefix'
        | 'pageSize'
        | 'fetchPage'
        | 'parseCursorParam'
        | 'fieldPredicates'
        | 'jsonPathPredicates'
        | 'initialQueryText'
        | 'enabled'
        | 'retry'
        | 'staleTime'
        | 'gcTime'
        | 'rootMargin'
        | 'threshold'
    > {
    pageTitle: string;
    cardTitle?: string;
    cardId?: string;
    queryPlaceholder: string;
    stateCopy?: Partial<InfiniteScrollListStateCopy>;
    headerActions?: ReactNode;
    cardHeaderActions?: ReactNode;
    controls?: ReactNode;
    predicateControls?: InfiniteScrollListPredicateDescriptorSet;
    controlsPlacement?: 'inline' | 'above-search';
    showQueryInput?: boolean;
    showPageHeader?: boolean;
    compactBottomSpacing?: boolean;
    renderItems: (
        context: InfiniteScrollListRenderContext<TItem, TCursor>
    ) => ReactNode;
}

export function InfiniteScrollList<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
>(props: InfiniteScrollListProps<TItem, TCursor, TQueryKeyPrefix>): ReactElement {
    const stateCopy: InfiniteScrollListStateCopy = {
        ...DefaultInfiniteScrollListStateCopy,
        ...props.stateCopy,
    };
    const [generatedPredicates, setGeneratedPredicates] = useState<{
        readonly fieldPredicates: ReadonlyArray<SearchFieldPredicate>;
        readonly jsonPathPredicates: ReadonlyArray<SearchJsonPathPredicate>;
    }>({
        fieldPredicates: [],
        jsonPathPredicates: [],
    });
    const fieldPredicates = useMemo(
        () => [
            ...(props.fieldPredicates ?? []),
            ...generatedPredicates.fieldPredicates,
        ],
        [props.fieldPredicates, generatedPredicates.fieldPredicates]
    );
    const jsonPathPredicates = useMemo(
        () => [
            ...(props.jsonPathPredicates ?? []),
            ...generatedPredicates.jsonPathPredicates,
        ],
        [props.jsonPathPredicates, generatedPredicates.jsonPathPredicates]
    );
    const list = useInfiniteScrollList<TItem, TCursor, TQueryKeyPrefix>({
        queryKeyPrefix: props.queryKeyPrefix,
        pageSize: props.pageSize,
        fetchPage: props.fetchPage,
        parseCursorParam: props.parseCursorParam,
        fieldPredicates: fieldPredicates.length > 0 ? fieldPredicates : undefined,
        jsonPathPredicates:
            jsonPathPredicates.length > 0 ? jsonPathPredicates : undefined,
        initialQueryText: props.initialQueryText,
        enabled: props.enabled,
        retry: props.retry,
        staleTime: props.staleTime,
        gcTime: props.gcTime,
        rootMargin: props.rootMargin,
        threshold: props.threshold,
    });

    return (
        <div>
            {props.showPageHeader !== false ? (
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="text-3xl font-bold">{props.pageTitle}</h1>
                    {props.headerActions ?? null}
                </div>
            ) : null}

            <Card id={props.cardId}>
                {props.cardTitle !== undefined ? (
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{props.cardTitle}</CardTitle>
                        {props.cardHeaderActions ?? null}
                    </CardHeader>
                ) : null}
                <CardContent>
                    {props.controlsPlacement === 'above-search' ? (
                        <div className="mb-4 flex flex-col gap-3">
                            {props.controls ?? null}
                            {props.predicateControls !== undefined ? (
                                <ListPredicateControls
                                    fieldPredicateDescriptors={
                                        props.predicateControls.fieldPredicates
                                    }
                                    jsonPathPredicateDescriptors={
                                        props.predicateControls.jsonPathPredicates
                                    }
                                    onChange={setGeneratedPredicates}
                                />
                            ) : null}
                            {props.showQueryInput !== false ? (
                                <Input
                                    placeholder={props.queryPlaceholder}
                                    value={list.queryText}
                                    onChange={(event) => {
                                        list.setQueryText(event.target.value);
                                    }}
                                    className="w-[320px]"
                                />
                            ) : null}
                        </div>
                    ) : (
                        <div className="mb-4 flex items-center justify-between gap-4">
                            {props.showQueryInput !== false ? (
                                <Input
                                    placeholder={props.queryPlaceholder}
                                    value={list.queryText}
                                    onChange={(event) => {
                                        list.setQueryText(event.target.value);
                                    }}
                                    className="w-[320px]"
                                />
                            ) : null}
                            <div className="flex flex-col gap-3">
                                {props.controls ?? null}
                                {props.predicateControls !== undefined ? (
                                    <ListPredicateControls
                                        fieldPredicateDescriptors={
                                            props.predicateControls.fieldPredicates
                                        }
                                        jsonPathPredicateDescriptors={
                                            props.predicateControls.jsonPathPredicates
                                        }
                                        onChange={setGeneratedPredicates}
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}

                    {list.error !== null ? (
                        <InfiniteScrollListErrorState
                            error={list.error}
                            genericErrorLabel={stateCopy.genericErrorLabel}
                            onRetry={list.retry}
                        />
                    ) : list.isLoading && list.items.length === 0 ? (
                        <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
                    ) : list.items.length === 0 ? (
                        <InfiniteScrollListEmptyState label={stateCopy.emptyLabel} />
                    ) : (
                        <>
                            {props.renderItems({
                                items: list.items,
                                isFetching: list.isFetching,
                                isFetchingNextPage: list.isFetchingNextPage,
                                hasNextPage: list.hasNextPage,
                                pagination: list.pagination,
                            })}
                            <div
                                ref={list.loadMoreSentinelRef}
                                className={`${props.compactBottomSpacing ? 'h-0' : 'h-8'} [overflow-anchor:none]`}
                            />
                            {list.isFetching ? (
                                <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
                            ) : null}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
